import FormInputError from "components/General/FormInputError";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
export default function PaymentPayor({
  register,
  validate,
  getFieldState,
  errors,
  setCurrentStep,
  getValues,
  isNonNaturalEntity,
}) {
  // debounce function config
  let debounceFunction; //timer identifier
  let debounceTimer = 100; //time in ms, 0.1 seconds for example
  const { t } = useTranslation();
  const fNameId = uuidv4();
  const lNameId = uuidv4();
  const mInitialID = uuidv4();
  const emailID = uuidv4();
  const zipID = uuidv4();
  const mobileID = uuidv4();

  useEffect(() => {
    if (isNonNaturalEntity === true) {
      setCurrentStep(5);
    }
  }, [isNonNaturalEntity]);

  // make sure all payor require values has input before enabling submit
  const validatePayor = async () => {
    if (isNonNaturalEntity) {
      setCurrentStep(5);
      return;
    }
    const formHasError =
      getFieldState("firstName").error ||
      getFieldState("lastName").error ||
      getFieldState("zipcode").error ||
      getFieldState("emailAddress").error ||
      !getValues("firstName") ||
      !getValues("lastName") ||
      !getValues("zipcode") ||
      !getValues("emailAddress");
    if (!formHasError) {
      setCurrentStep(5);
    } else {
      setCurrentStep(4);
    }
  };

  return (
    <>
      <div className="ui-form-card-no-event m-0 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div>
          <label
            id={fNameId + "-label"}
            htmlFor={fNameId}
            className="block mb-1"
          >
            {isNonNaturalEntity
              ? "First name"
              : t("AgentPaymentPayor-FirstName")}
          </label>
          <input
            id={fNameId}
            aria-labelledby={fNameId + "-label"}
            aria-describedby={fNameId + "-error"}
            type="text"
            {...register(
              "firstName",
              isNonNaturalEntity
                ? {}
                : {
                    required: t("AgentPaymentPayor-EnterFirstName"),
                    maxLength: {
                      value: 50,
                      message: t("AgentPaymentPayor-EnterFirstName-Desc"),
                    },
                    pattern: {
                      value: /^[A-Za-z-' ]{1,50}$/,
                      message: t("AgentPaymentPayor-EnterOnlyAlphaCharacters"),
                    },
                  }
            )}
            onKeyUp={() => {
              clearTimeout(debounceFunction);
              debounceFunction = setTimeout(async () => {
                await validate("firstName");
                await validatePayor();
              }, debounceTimer);
            }}
            onKeyDown={() => {
              clearTimeout(debounceFunction);
            }}
          />
          <div id={fNameId + "-error"}>
            {errors.firstName?.message && (
              <FormInputError errorMessage={errors.firstName.message} />
            )}
          </div>
        </div>
        <div>
          <label
            id={lNameId + "-label"}
            htmlFor={lNameId}
            className="block mb-1"
          >
            {isNonNaturalEntity ? "Last name" : t("AgentPaymentPayor-LastName")}
          </label>
          <input
            id={lNameId}
            aria-labelledby={lNameId + "-label"}
            aria-describedby={lNameId + "-error"}
            type="text"
            {...register(
              "lastName",
              isNonNaturalEntity
                ? {}
                : {
                    required: t("AgentPaymentPayor-EnterLastName"),
                    maxLength: {
                      value: 50,
                      message: t("AgentPaymentPayor-EnterLastName-Desc"),
                    },
                    pattern: {
                      value: /^[A-Za-z-' ]{1,50}$/,
                      message: t("AgentPaymentPayor-EnterOnlyAlpha"),
                    },
                  }
            )}
            onKeyUp={() => {
              clearTimeout(debounceFunction);
              debounceFunction = setTimeout(async () => {
                await validate("lastName");
                await validatePayor();
              }, debounceTimer);
            }}
            onKeyDown={() => {
              clearTimeout(debounceFunction);
            }}
          />
          <div id={lNameId + "-error"}>
            {errors.lastName?.message && (
              <FormInputError errorMessage={errors.lastName.message} />
            )}
          </div>
        </div>
        <div>
          <label
            id={mInitialID + "-label"}
            htmlFor={mInitialID}
            className="block mb-1"
          >
            {t("AgentPaymentPayor-MiddleInitial")}
          </label>
          <input
            id={mInitialID}
            aria-labelledby={mInitialID + "-label"}
            type="text"
            {...register("mInitial")}
          />
        </div>
        <div>
          <label
            id={emailID + "-label"}
            htmlFor={emailID}
            className="block mb-1"
          >
            {isNonNaturalEntity
              ? "Email address"
              : t("AgentPaymentPayor-Email")}
          </label>
          <input
            id={emailID}
            aria-labelledby={emailID + "-label"}
            aria-describedby={`${emailID + "-help"} ${emailID + "-error"}`}
            type="text"
            {...register(
              "emailAddress",
              isNonNaturalEntity
                ? {}
                : {
                    required: {
                      value: true,
                      message: t("AgentPaymentPayor-EnterEmailAddress"),
                    },
                    pattern: {
                      value: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
                      message: t("AgentPaymentPayor-EnterValidEmail"),
                    },
                  }
            )}
            onKeyUp={() => {
              clearTimeout(debounceFunction);
              debounceFunction = setTimeout(async () => {
                await validate("emailAddress");
                await validatePayor();
              }, debounceTimer);
            }}
            onKeyDown={() => {
              clearTimeout(debounceFunction);
            }}
          />
          <div id={emailID + "-error"}>
            {errors.emailAddress?.message && (
              <FormInputError errorMessage={errors.emailAddress.message} />
            )}
          </div>
          <div
            id={emailID + "-help"}
            className="text-xxs text-theme-text-tertiary mt-1"
          >
            {t("AgentPaymentPayor-FromatRequirement")}
          </div>
        </div>
        <div>
          <label id={zipID + "-label"} htmlFor={zipID} className="block mb-1">
            {isNonNaturalEntity ? "Zip Code" : "Zip Code *"}
          </label>
          <input
            id={zipID}
            aria-labelledby={zipID + "-label"}
            aria-describedby={zipID + "-error"}
            type="text"
            {...register(
              "zipcode",
              isNonNaturalEntity
                ? {}
                : {
                    required: {
                      value: true,
                      message: t("AgentPaymentPayor-EnterZipcode"),
                    },
                    pattern: {
                      value: /^\d{5}$/,
                      message: t("AgentPaymentPayor-EnterValidZipcode"),
                    },
                    maxLength: {
                      value: 5,
                      message: t("AgentPaymentPayor-Enter5DigitsZipcode"),
                    },
                  }
            )}
            onKeyUp={() => {
              clearTimeout(debounceFunction);
              debounceFunction = setTimeout(async () => {
                await validate("zipcode");
                await validatePayor();
              }, debounceTimer);
            }}
            onKeyDown={() => {
              clearTimeout(debounceFunction);
            }}
          />
          <div id={zipID + "-error"}>
            {errors.zipcode?.message && (
              <FormInputError errorMessage={errors.zipcode.message} />
            )}
          </div>
        </div>
        <div>
          <label
            id={mobileID + "-label"}
            htmlFor={mobileID}
            className="block mb-1"
          >
            {t("AgentPaymentPayor-MobileNumber")}
          </label>
          <input
            id={mobileID}
            aria-labelledby={mobileID + "-label"}
            aria-describedby={mobileID + "-help"}
            type="text"
            {...register("mobilePhone")}
          />
          <div
            id={mobileID + "-help"}
            className="text-xxs text-theme-text-tertiary mt-1"
          >
            {t("AgentPaymentPayor-FromatRequirementPhone")}
          </div>
        </div>
      </div>
    </>
  );
}
